import {DvViewItemType, increasePocketVersion, PocketSource} from '@dv5/domain'
import type {PocketContent, PocketItem, PocketSourceFileData, PocketSourceItemData} from '@dv5/domain'
import {Dispatch} from "react"

const STORAGE_KEY = "dv5-pocket"

class PocketService {

    getPocketItems(): Array<PocketItem> {
        const content = this.getPocketContent()
        return content.items
    }

    getTooltipText(itemCount: number): string {
        if (itemCount === 0) {
            return 'Pocket is empty'
        }
        if (itemCount === 1) {
            return '1 item in Pocket'
        }
        return `${itemCount} items in Pocket`
    }

    putIntoPocket(item: PocketItem, dispatch: Dispatch<any>): void {
        const content = this.getPocketContent()
        content.items.push(item)
        content.selectedIndex = content.items.length - 1
        this.savePocketContent(content)
        dispatch(increasePocketVersion())
    }

    putFileIntoPocket(file: PocketSourceFileData, dispatch: Dispatch<any>): void {
        const content = this.getPocketContent()
        const item: PocketItem = {
            id: crypto.randomUUID(),
            source: PocketSource.FILE,
            sourceId: file.id,
            sourceData: file,
            title: file.name
        }
        content.items.push(item)
        content.selectedIndex = content.items.length - 1
        this.savePocketContent(content)
        dispatch(increasePocketVersion())
    }

    hasItemToAdd(): boolean {
        const content = this.getPocketContent()
        let checkIndex = content.selectedIndex || 0
        if (checkIndex >= content.items.length) {
            checkIndex = 0
        }
        return content.items.length > checkIndex && (content.items[checkIndex].source === PocketSource.VIEW_ITEM
            || content.items[checkIndex].source === PocketSource.MANUAL)
    }

    getPastingViewItem(): PocketItem | null {
        const content = this.getPocketContent()
        let checkIndex = content.selectedIndex || 0
        if (checkIndex > content.items.length) {
            checkIndex = 0
        }
        if (content.items.length > checkIndex) {
            if (content.items[checkIndex].source === PocketSource.VIEW_ITEM) {
                return content.items[checkIndex]
            }
        }
        return null
    }

    getSelectedItem(): PocketItem | null {
        const content = this.getPocketContent()
        let checkIndex = content.selectedIndex || 0
        if (checkIndex > content.items.length) {
            checkIndex = 0
        }
        if (content.items.length > checkIndex) {
            return content.items[checkIndex]
        }
        return null
    }

    getLinkableItem(): PocketItem | null {
        const content = this.getPocketContent()
        const checkIndex = content.selectedIndex || 0
        if (checkIndex >= content.items.length) {
            return null
        }
        const item = this.getSelectedItem()
        if (item && (item.source === PocketSource.VIEW_ITEM || item.source === PocketSource.FILE)) {
            return item
        }
        return null
    }

    hasLinkableItem(): boolean {
        return this.getLinkableItem() !== null
    }

    removeViewItem(dispatch: Dispatch<any>) {
        const content = this.getPocketContent()
        const newItems: Array<PocketItem> = []
        let removed = false
        for (const item of content.items) {
            if (item.source === PocketSource.VIEW_ITEM && !removed) {
                removed = true
            } else {
                newItems.push(item)
            }
        }
        content.items = newItems
        content.selectedIndex = content.items.length === 0 ? undefined : 0
        this.savePocketContent(content)
        dispatch(increasePocketVersion())
    }

    removeSelectedItem(dispatch: Dispatch<any>) {
        const selectedItem = this.getSelectedItem()
        if (selectedItem) {
            const content = this.getPocketContent()
            const newItems: Array<PocketItem> = []
            let removed = false
            for (const item of content.items) {
                if (item.id === selectedItem.id && !removed) {
                    removed = true
                } else {
                    newItems.push(item)
                }
            }
            content.items = newItems
            content.selectedIndex = content.items.length === 0 ? undefined : 0
            this.savePocketContent(content)
            dispatch(increasePocketVersion())
        }
    }

    clear(dispatch: Dispatch<any>) {
        const content: PocketContent = {items: []}
        content.selectedIndex = undefined
        this.savePocketContent(content)
        dispatch(increasePocketVersion())
    }

    getSelectedIndex(): number | undefined {
        const content = this.getPocketContent()
        return content.selectedIndex
    }

    setSelectedIndex(newIndex: number, dispatch: Dispatch<any>) {
        const content = this.getPocketContent()
        if (newIndex < content.items.length) {
            content.selectedIndex = newIndex
            this.savePocketContent(content)
            dispatch(increasePocketVersion())
        }
    }

    addManualItem(title: string, itemType: DvViewItemType, dispatch: Dispatch<any>) {
        const content = this.getPocketContent()
        const manualItemData: PocketSourceItemData = {
            id: crypto.randomUUID(),
            type: itemType
        }
        const item: PocketItem = {
            id: crypto.randomUUID(),
            source: PocketSource.MANUAL,
            sourceId: '',
            sourceData: manualItemData,
            title
        }
        content.items.push(item)
        content.selectedIndex = content.items.length - 1
        this.savePocketContent(content)
        dispatch(increasePocketVersion())
    }

    getDataType(pocketItem: PocketItem): DvViewItemType | undefined {
        const sourceData = pocketItem.sourceData
        if ('data' in sourceData) {
            const data: any = sourceData.data
            if ('type' in data) {
                return data.type as DvViewItemType
            }
        } else if ('type' in sourceData) {
            return sourceData.type as DvViewItemType
        }
        return undefined
    }

    private getPocketContent(): PocketContent {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (!stored) {
            return {items: []}
        }
        try {
            return JSON.parse(stored) as PocketContent
        } catch {
            return {items: []}
        }
    }

    private savePocketContent(content: PocketContent): void {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(content))
    }
}

export const pocketService = new PocketService()
